<!--
  @component
  Generates an SVG Sankey chart using [d3-sankey](https://github.com/d3/d3-sankey).
 -->
<script>
  import { getContext } from "svelte";
  import * as Sankey from "d3-sankey";

  const { data, width, height } = getContext("LayerCake");

  let sankeyData;

  function my_node_color(d) {
    if ("color" in d) {
      return d["color"];
    }

    return "#6fcdfa";
  }

  function my_link_color(d) {
    /*
    index: 5
    source: {id: 'B1', index: 3, sourceLinks: Array(3), targetLinks: Array(1), value: 27, …}
    target: {id: 'C1', index: 7, sourceLinks: Array(0), targetLinks: Array(1), value: 13, …}
    value: 13
    width: 77.16129032258064
    y0: 38.58064516129032
    y1: 38.58064516129032
    */

    if ("color" in d) {
      return d["color"];
    }

    return "rgba(40, 20, 0, .2)";
  }

  // * @type {Function} [colorLinks=d => 'rgba(0, 0, 0, .2)'] – A function to return a color for the links.

  /** @type {Number} [nodeWidth=5] – The width of each node, in pixels, passed to [`sankey.nodeWidth`](https://github.com/d3/d3-sankey#sankey_nodeWidth). */
  export let nodeWidth = 5;

  /** @type {Number} [nodePadding=10] – The padding between nodes, passed to [`sankey.nodePadding`](https://github.com/d3/d3-sankey#sankey_nodePadding). */
  export let nodePadding = 10;

  /** @type {Function} [linkSort=null] – How to sort the links, passed to [`sankey.linkSort`](https://github.com/d3/d3-sankey#sankey_linkSort). */
  export let linkSort = null;

  /** @type {Function} [nodeId=d => d.id] – The ID field accessor, passed to [`sankey.nodeId`](https://github.com/d3/d3-sankey#sankey_nodeId). */
  export let nodeId = (d) => d.id;

  /** @type {Function} [nodeAlign=d3.sankeyLeft] – An alignment function to position the Sankey blocks. See the [d3-sankey documentation](https://github.com/d3/d3-sankey#alignments) for more. */
  export let nodeAlign = Sankey.sankeyLeft;

  $: sankey = Sankey.sankey()
    .nodeAlign(nodeAlign)
    .nodeWidth(nodeWidth)
    .nodePadding(nodePadding)
    .nodeId(nodeId)
    .size([$width, $height])
    .linkSort(linkSort);

  // $: sankeyData = sankey($data);
  $: {
    data;
    // console.log('data in sankey: ', data);
    sankeyData = sankey($data);
  }

  // console.log("sankeyData: ", sankeyData)

  $: link = Sankey.sankeyLinkHorizontal();

  $: fontSize = $width <= 320 ? 12 : 18;

  $: fontSize_small = fontSize - 5;

  let breite = 50;
  let factor = 0.9;

  function custom_link(d) {
    // console.log("d: ", d);
    if (isNaN(d["source"]["y0"])) {
      d["source"]["y0"] = 0;
    }
    if (isNaN(d["source"]["y1"])) {
      d["source"]["y1"] = 0;
    }
    if (isNaN(d["target"]["y0"])) {
      d["target"]["y0"] = 0;
    }
    if (isNaN(d["target"]["y1"])) {
      d["target"]["y1"] = 0;
    }
    if (isNaN(d["y0"])) {
      d["y0"] = 0;
    }
    if (isNaN(d["y1"])) {
      d["y1"] = 0;
    }
    if (isNaN(d["width"])) {
      d["width"] = 0;
    }

    var link_str = link(d);
    var x_str = link(d).split(",", 1)[0];
    x_str = x_str.substring(1);

    //startpunkt
    var x = parseFloat(x_str) * factor;
    x = (x + breite) * factor;
    // console.log("x: ", x)
    x = d["source"]["x1"] * factor;
    // console.log("x neu: ", x)
    link_str = link_str.substring(link_str.indexOf(",") + 1);
    link_str = "M" + x.toString() + "," + link_str;

    //endpunkt:
    var x_end_str = link(d).split(",")[5];

    var x_end = parseFloat(x_end_str) * factor;
    x_end = d["target"]["x0"] * factor;

    var link_str_arr = link_str.split(",");
    link_str_arr[5] = x_end.toString();

    link_str = link_str_arr.join(",");

    // console.log("data: ", data)

    return link_str;
  }
</script>

<!-- in text:

x={(d.x0 < $width / 4 ? d.x1 + 6 : d.x0 - 6)*factor} -->

<g class="sankey-layer">
  <g class="link-group">
    {#each sankeyData.links as d}
      <path d={custom_link(d)} fill="none" stroke={my_link_color(d)} stroke-opacity="0.5" stroke-width={d.width} />
      {#if "show_link_text" in d && d.show_link_text}
        <text
          x={((d.source.x1 + d.target.x0) / 2) * factor}
          y={((d.target.y1 + d.target.y0) / 2) * factor}
          transform="translate(0,+22)"
          style="fill: #4d6659;
              font-size: {fontSize_small}px;
              "
        >
          {d.value}
        </text>
      {/if}
    {/each}
  </g>
  <g class="rect-group">
    {#each sankeyData.nodes as d, i}
      <rect
        style="stroke-width:3; stroke:#8d8c89"
        x={d.x0 * factor}
        y={d.y0}
        height={d.y1 - d.y0}
        width={(d.x1 - d.x0 + breite) * factor}
        fill={my_node_color(d)}
      />
      {#if d.y1 - d.y0 > 35}
        <text
          x={(d.x0 < $width / 4 ? d.x1 + 20 : d.x0 + 25) * factor}
          y={(d.y1 + d.y0) / 2}
          transform="rotate(90,{(d.x0 < $width / 4 ? d.x1 + 20 : d.x0 + 25) * factor},{(d.y1 + d.y0) / 2}) translate(-{d.extra.length *
            5},5)"
          style="fill: #4d6659;
              font-size: {fontSize}px;
              "
        >
          {#if !d.extra}
            {d.id}
          {:else}
            {d.extra}
          {/if}
        </text>
      {/if}

      {#if d.y1 - d.y0 <= 35 && d.y1 - d.y0 >= 15}
        <text
          x={((d.x0 < $width / 4 ? d.x1 + 20 : d.x0 + 25) - 17) * factor}
          y={(d.y1 + d.y0) / 2}
          transform="translate(0,5)"
          style="fill: #4d6659;
              font-size: {fontSize}px;
              "
        >
          {#if !d.extra}
            {d.id}
          {:else}
            {d.extra}
          {/if}
        </text>
      {/if}
    {/each}
  </g>
</g>

<style>
  text {
    pointer-events: none;
  }
</style>
